/**
 * The one-time credentials panel.
 *
 * Shown after a profile is created and again after its credentials are replaced
 * — the same screen either way, because it is the same moment: values the server
 * will not show again, and the owner has one chance to record them. Sharing the
 * module keeps the wording and the printable card identical in both, which is
 * what stops the two flows drifting into subtly different advice.
 *
 * The panel shows the PIN; the printed card never does. That split is the whole
 * security argument for putting the phrase on paper and in a QR code, so the
 * two must be built together, here, rather than assembled separately by each
 * caller.
 */

import { el, icon } from "./dom.js";
import { errorAlert, successAlert, section } from "./patterns.js";
import { renderPrintCard, renderQrCode } from "./print-card.js";
import { downloadPrintFile } from "./print-pdf.js";
import { printPartnerNotice } from "./print-partner-notice.js";

/**
 * Build the credentials panel, with a printable card when there is a phrase.
 *
 * Rows appear only for the values actually issued. That is not cosmetic: after a
 * PIN-only reset the phrase is unchanged, every printed card still works, and
 * offering to reprint one would be wrong — so the card is attached only when a
 * phrase is present.
 *
 * @param {object} options
 * @param {string} options.heading Success message at the top of the panel.
 * @param {string} options.label The profile's name, printed on the card.
 * @param {string | undefined} [options.phrase] The phrase, when one has just been issued.
 * @param {string | undefined} [options.pin] The PIN, when one has just been issued.
 * @param {string} options.intro Plain-language explanation above the values.
 * @param {string} [options.pinNote] An extra note shown under the PIN row —
 *   used only at creation, where it says the PIN can be changed later. Absent
 *   after a credentials reset, where that would be redundant.
 * @param {Node[]} [options.actions] Buttons appended after "Print the card".
 * @returns {DocumentFragment}
 */
export function renderCredentialsPanel({
  heading,
  label,
  phrase,
  pin,
  intro,
  pinNote,
  actions = [],
}) {
  const frag = document.createDocumentFragment();
  frag.append(successAlert(heading));

  const rows = [];
  if (phrase) {
    rows.push(
      el("dt", {}, ["Recovery phrase"]),
      el("dd", {}, [
        el("code", {}, [phrase]),
        el("wa-copy-button", { value: phrase, "copy-label": "Copy phrase" }),
      ]),
    );
  }
  if (pin) {
    rows.push(
      el("dt", {}, ["PIN"]),
      el("dd", {}, [
        el("code", {}, [pin]),
        el("wa-copy-button", { value: pin, "copy-label": "Copy PIN" }),
        pinNote ? el("p", { class: "kin-note" }, [pinNote]) : null,
      ]),
    );
  }

  const body = [
    el("p", { class: "kin-prose" }, [intro]),
    el("dl", { class: "kin-credentials" }, rows),
  ];

  if (phrase) {
    // The same code that goes on the card, on screen as well, so the owner can
    // scan it and see for themselves what it does before trusting it to paper.
    body.push(
      el("div", { class: "kin-credential-qr" }, [
        renderQrCode(phrase, { size: 132 }),
        el("p", { class: "kin-note" }, [
          "This is the code printed on the card. Scanning it opens KinCode with the " +
            "phrase already filled in — the PIN still has to be typed.",
        ]),
      ]),
      el("p", { class: "kin-prose kin-note" }, [
        "The card and its code carry the phrase but not the PIN, so a card that is " +
          "lost or photographed is not enough to retrieve your contacts. Share the " +
          "PIN separately with the people who need it. Treat the phrase like a " +
          "username: anyone holding the card can read it.",
      ]),
    );
  }

  // Announces the outcome of generating a print file, which produces no visible
  // change on the page — the file simply arrives in the browser's downloads.
  const printFileStatus = el("div", { "aria-live": "assertive" });

  body.push(
    el("div", { class: "kin-cta-row" }, [
      phrase
        ? el("wa-button", { appearance: "outlined", on: { click: () => window.print() } }, [
            icon("print"),
            "Print the card",
          ])
        : null,
      // Offered here because this is the one moment the phrase is already in
      // hand. Everywhere else the owner has to type it back in, since the server
      // keeps only its HMAC.
      phrase ? printFileButton({ label, phrase, status: printFileStatus }) : null,
      ...actions,
    ]),
    printFileStatus,
  );

  // Only alongside the download option it refers to, not the on-printer route.
  if (phrase) {
    body.push(printPartnerNotice());
  }

  frag.append(section("Keep these safe", body));

  if (phrase) {
    frag.append(renderPrintCard({ label, phrase }));
  }

  return frag;
}

/**
 * The "Download print file" button, with its busy and outcome states.
 *
 * Generating takes a moment — a megabyte of fonts on first use, then the PDF —
 * and it ends with no visible change to the page, so both the busy state and the
 * completion have to be announced or a screen-reader user has no way to tell
 * whether anything happened.
 *
 * @param {{ label: string, phrase: string, status: HTMLElement }} options
 * @returns {HTMLElement}
 */
export function printFileButton({ label, phrase, status }) {
  const button = el(
    "wa-button",
    {
      appearance: "outlined",
      on: {
        click: async () => {
          status.replaceChildren();
          button.setAttribute("loading", "");
          try {
            await downloadPrintFile({ label, phrase });
            status.replaceChildren(successAlert("Your print file has been downloaded (PDF)."));
          } catch (error) {
            status.replaceChildren(
              errorAlert(
                error instanceof Error
                  ? error.message
                  : "Could not create the print file. Please try again.",
              ),
            );
          } finally {
            button.removeAttribute("loading");
          }
        },
      },
    },
    [icon("print"), "Download print file (PDF)"],
  );
  return button;
}
